import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData, postData } from "../../../FetchApiServices";
import { useToast } from "../Layout/T2TUIProvider";
import VendorChangesDiff from "../Vendor/VendorChangesDiff";
import VendorCreationForm from "../Vendor/VendorCreationForm";
import { getAccessMatrix } from "../../../api/t2tVendorFieldAccessApi";

const VENDOR_RT_CODES = new Set(["vendor_creation","vendor_change","vendor_extend","vendor_creation_seasonal"]);

// Map requestTypeCode → workflow_action for field-access API
const rtToAction = (code) => {
  if (code === "vendor_change")  return "change";
  if (code === "vendor_extend")  return "extend";
  return "create";
};

// Map current stage name → role_code for field-access API
const stageToRole = (stageName) => {
  if (!stageName) return "initiator";
  const n = stageName.toLowerCase();
  if (n.includes("mdm"))     return "mdm_approver";
  if (n.includes("finance")) return "finance";
  if (n.includes("checker")) return "checker";
  return "initiator";
};

// ARCH-06: request-type codes for which procurement-flavour widgets
// (Three-Way Match, e-Invoice, Ariba log, GeM order, TReDS) are irrelevant.
// Extend this list as new non-procurement request types are added.
const HIDE_PROCUREMENT_WIDGETS_FOR = new Set([
  "vendor_creation",
  "leave_request",
  "hr_onboarding",
]);
const isHiddenProcurementType = (code) => !!code && HIDE_PROCUREMENT_WIDGETS_FOR.has(code);

const T2TRequestDetail = () => {
  const navigate = useNavigate();
  const toast    = useToast();
  const { id }   = useParams();

  const [req, setReq]               = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [actionModal, setActionModal] = useState(null); // 'approve' | 'reject' | 'reassign' | 'hold'
  const [resumeDate, setResumeDate]   = useState("");
  const [comment, setComment]       = useState("");
  const [actionError, setActionError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Phase 9 — Rejection routing
  const [rejTargets, setRejTargets]     = useState([]);
  const [rejTargetId, setRejTargetId]   = useState("");
  const [rejLoading, setRejLoading]     = useState(false);

  // Phase 13 — Attachments
  const [attachments, setAttachments]   = useState([]);
  // Phase 19 — Verifications
  const [verifications, setVerifications] = useState([]);
  // Phase 18 — SAP push logs
  const [sapLogs, setSapLogs] = useState([]);
  // INT-7/9 — Three-way match
  const [threeWayMatch, setThreeWayMatch] = useState(null);
  // INT-8 — e-Invoice IRN
  const [einvoice, setEinvoice] = useState(null);
  // INT-10/11 — Ariba logs + sourcing
  const [aribaLogs, setAribaLogs] = useState([]);
  // INT-14 — GeM order status
  const [gemStatus, setGemStatus] = useState(null);
  // INT-15 — TReDS status
  const [tredsStatus, setTredsStatus] = useState(null);
  // Field label map (field_id → label) from workflow form schema
  const [fieldLabelMap, setFieldLabelMap] = useState({});
  // ARCH-06: request type code, used to hide irrelevant procurement widgets.
  const [requestTypeCode, setRequestTypeCode] = useState(null);
  // T2T-03-WF1: vendor change diff
  const [vendorForDiff, setVendorForDiff] = useState(null);
  const [blockVendor, setBlockVendor] = useState(false);
  // T2T-03-MM1: mismatch severity gating
  const [mismatchSummary, setMismatchSummary]       = useState(null); // { worst_severity, mismatches[] }
  const [mismatchAcknowledged, setMismatchAcknowledged] = useState(false);
  // Step 3: vendor form access matrix for approval view
  const [vendorAccessMatrix, setVendorAccessMatrix] = useState(null);

  useEffect(() => { fetchDetail(); fetchAttachments(); fetchVerifications(); fetchSapLogs(); /* fetchIntegrationData runs after requestTypeCode is known */ }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDetail = async () => {
    setLoading(true);
    setError("");
    const resp = await getData(`t2t/requests/${id}`);
    if (resp.error) {
      setError(resp.error.message || "Failed to load request.");
    } else {
      const d = resp.data?.data || resp.data || {};
      setReq(d);
      // Fetch workflow form fields to build label map
      if (d.workflow_id) {
        const wfResp = await getData(`t2t/workflows/${d.workflow_id}`);
        if (!wfResp.error) {
          const wfData = wfResp.data?.data || wfResp.data || {};
          // ARCH-02b-i fix-up: if workflow is attached to a Request Type, the fields
          // live there; otherwise fall back to legacy per-workflow form_fields.
          const fields =
            (wfData.request_type && Array.isArray(wfData.request_type.fields) && wfData.request_type.fields.length > 0)
              ? wfData.request_type.fields
              : (wfData.form_fields || wfData.fields || []);
          if (Array.isArray(fields)) {
            const map = {};
            fields.forEach((f) => {
              const key = String(f.field_id || f.id || "");
              if (key) map[key] = f.label || f.name || key;
            });
            setFieldLabelMap(map);
          }
          // ARCH-06: capture request type code so we can gate procurement widgets.
          const rtCode = wfData.request_type?.code || null;
          setRequestTypeCode(rtCode);
          // T2T-03-MM1: fetch mismatch severity summary for vendor workflow requests
          if (["vendor_creation","vendor_change","vendor_extend","vendor_seasonal"].includes(rtCode)) {
            getData(`t2t/requests/${id}/mismatch-summary`).then((mr) => {
              if (!mr.error) setMismatchSummary(mr.data?.data || null);
            });
          }
          // Step 3: fetch role-gated access matrix for vendor form in approval view
          if (VENDOR_RT_CODES.has(rtCode)) {
            const currentStageName = (d.stages || []).find((s) => s.current)?.name || null;
            const action   = rtToAction(rtCode);
            const roleCode = stageToRole(currentStageName);
            getAccessMatrix(action, roleCode).then((mr) => {
              if (!mr.error) {
                // API returns { group_code: access_level } map directly
                setVendorAccessMatrix(mr.data?.data || {});
              }
            });
          }
          // T2T-03-WF1: load vendor snapshot for change requests diff panel
          if (rtCode === "vendor_change") {
            const vendorId = d.form_data?._vendor_id;
            if (vendorId) {
              getData(`t2t/vendors/${vendorId}`).then((vr) => {
                if (!vr.error) setVendorForDiff(vr.data?.data || null);
              });
            }
          }
          // Only fetch procurement-flavour integration data if this request looks
          // procurement-ish. Legacy requests (no request type) keep fetching for back-compat.
          if (!isHiddenProcurementType(rtCode)) {
            fetchIntegrationData();
          }
        }
      }
    }
    setLoading(false);
  };

  // Phase 19 — fetch verifications
  const fetchVerifications = async () => {
    const resp = await getData(`t2t/requests/${id}/verifications`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setVerifications(Array.isArray(raw) ? raw : []);
    }
  };

  const VERIF_BADGES = {
    verified:        { icon: "✅", color: "#16A34A", bg: "#F0FDF4", label: "Verified" },
    name_mismatch:   { icon: "⚠️", color: "#D97706", bg: "#FFFBEB", label: "Name Mismatch" },
    invalid:         { icon: "❌", color: "#DC2626", bg: "#FEF2F2", label: "Invalid" },
    pending:         { icon: "🔄", color: "#64748B", bg: "#F8FAFC", label: "Pending" },
    cross_check_fail:{ icon: "⚡", color: "#DC2626", bg: "#FEF2F2", label: "Cross-Check Failed" },
    api_error:       { icon: "💸", color: "#EA580C", bg: "#FFF7ED", label: "API Error" },
  };

  // Phase 18 — fetch SAP push logs
  const fetchSapLogs = async () => {
    const resp = await getData(`t2t/requests/${id}/sap-log`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setSapLogs(Array.isArray(raw) ? raw : []);
    }
  };

  const retrySapPush = async () => {
    const resp = await postData(`t2t/requests/${id}/sap-retry`, {});
    if (resp.error) { toast(resp.error.message || "SAP retry failed.", "error"); }
    else { toast("SAP push retried.", "success"); fetchSapLogs(); }
  };

  // INT — fetch integration data (three-way match, e-invoice, ariba, gem, treds)
  const fetchIntegrationData = async () => {
    const [matchResp, einvResp, aribaResp, gemResp, tredsResp] = await Promise.all([
      getData(`t2t/procurement/three-way-match/${id}`),
      getData(`t2t/einvoice/${id}`),
      getData(`t2t/requests/${id}/ariba-log`),
      getData(`t2t/requests/${id}/gem-status`),
      getData(`t2t/requests/${id}/treds-status`),
    ]);
    if (!matchResp.error && matchResp.data?.data) setThreeWayMatch(matchResp.data.data);
    if (!einvResp.error && einvResp.data?.data) setEinvoice(einvResp.data.data);
    if (!aribaResp.error) setAribaLogs(Array.isArray(aribaResp.data?.data) ? aribaResp.data.data : []);
    if (!gemResp.error && gemResp.data?.data) setGemStatus(gemResp.data.data);
    if (!tredsResp.error && tredsResp.data?.data) setTredsStatus(tredsResp.data.data);
  };

  const cancelEinvoice = async () => {
    const resp = await postData(`t2t/einvoice/${id}/cancel`, {});
    if (resp.error) { toast(resp.error.message || "Failed to cancel IRN.", "error"); }
    else { toast("IRN cancelled.", "success"); fetchIntegrationData(); }
  };

  const retryAribaPush = async () => {
    const resp = await postData(`t2t/requests/${id}/ariba-retry`, {});
    if (resp.error) { toast(resp.error.message || "Ariba retry failed.", "error"); }
    else { toast("Ariba push retried.", "success"); fetchIntegrationData(); }
  };

  // Phase 13 — fetch attachments
  const fetchAttachments = async () => {
    const resp = await getData(`t2t/requests/${id}/attachments`);
    if (!resp.error) {
      const raw = resp.data?.data;
      setAttachments(Array.isArray(raw) ? raw : []);
    }
  };

  const downloadAttachment = async (attachId, filename) => {
    const resp = await getData(`t2t/attachments/${attachId}/download`);
    if (!resp.error) {
      const url = resp.data?.data?.download_url || resp.data?.data?.url;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "download";
        a.target = "_blank";
        a.click();
      }
    } else {
      toast("Failed to get download link.", "error");
    }
  };

  // R12 — comment ≥ 5 chars required for approve / reject / hold
  const handleAction = async () => {
    if (["approve", "reject", "hold"].includes(actionModal) && comment.trim().length < 5) {
      setActionError("Please add a comment (at least 5 characters).");
      return;
    }
    // Phase 9 — reject requires a return stage
    if (actionModal === "reject" && !rejTargetId) {
      setActionError("Please select a stage to return the request to.");
      return;
    }
    setSubmitting(true);

    let resp;
    if (actionModal === "reject") {
      resp = await postData(`t2t/requests/${id}/reject`, {
        return_to_stage_id: rejTargetId,
        comment:            comment.trim(),
      });
    } else if (actionModal === "hold") {
      resp = await postData(`t2t/requests/${id}/hold`, {
        comment:         comment.trim(),
        expected_resume: resumeDate || null,
      });
    } else {
      resp = await postData(`t2t/requests/${id}/action`, {
        action:       actionModal,
        comment:      comment.trim(),
        reassign_to:  null,
        block_vendor: actionModal === "approve" && blockVendor ? true : undefined,
      });
    }

    if (resp.error) {
      setActionError(resp.error.message || "Action failed. Please try again.");
      setSubmitting(false);
      return;
    }
    setActionModal(null);
    setComment("");
    setActionError("");
    setSubmitting(false);
    setRejTargetId("");
    setResumeDate("");
    setBlockVendor(false);
    const msgs = { approve: "Request approved.", reject: "Request rejected and returned.", hold: "Request put on hold.", reassign: "Request reassigned." };
    toast(msgs[actionModal] || "Action completed.", "success");
    if (actionModal === "hold") { fetchDetail(); } else { navigate("/t2t-requests"); }
  };

  // Unhold — lift a hold and resume the request
  const handleUnhold = async () => {
    setSubmitting(true);
    const resp = await postData(`t2t/requests/${id}/unhold`, {});
    if (resp.error) { toast(resp.error.message || "Failed to resume.", "error"); setSubmitting(false); return; }
    toast("Hold lifted. Request resumed.", "success");
    setSubmitting(false);
    fetchDetail();
  };

  const DOT_CLASS = {
    submit: "submit", approve: "approve", reject: "reject", reassign: "reassign",
    system_action_execute: "reassign",
    system_action_success: "approve",
    system_action_failure: "reject",
  };

  if (loading) {
    return (
      <>
        <T2TTopNav title="Request" actions={<button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-requests")}>← Back</button>} />
        <div className="t2t-content" style={{ textAlign: "center", paddingTop: 60, color: "var(--t2t-text-muted)", fontSize: 13 }}>Loading...</div>
      </>
    );
  }

  if (error || !req) {
    return (
      <>
        <T2TTopNav title="Request" actions={<button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-requests")}>← Back</button>} />
        <div className="t2t-content">
          <div style={{ padding: "10px 14px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12 }}>
            {error || "Request not found."}
          </div>
        </div>
      </>
    );
  }

  const stages   = req.stages   || [];
  const formData = req.form_data || {};
  const timeline = req.timeline  || [];

  return (
    <>
      <T2TTopNav
        title={req.request_id || id}
        actions={
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t-requests")}>
            ← Back
          </button>
        }
      />
      <div className="t2t-req-detail">
        {/* Main */}
        <div className="t2t-req-main">
          {/* On-Hold Banner */}
          {req.status === "on_hold" && (
            <div style={{ padding: "12px 16px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 20 }}>⏸</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#92400E" }}>This request is on hold</div>
                <div style={{ fontSize: 12, color: "#78350F" }}>
                  {req.hold_reason && `Reason: ${req.hold_reason}`}
                  {req.expected_resume && ` • Expected resume: ${req.expected_resume}`}
                </div>
              </div>
              {req.can_act && (
                <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={handleUnhold} disabled={submitting}>
                  ▶ Resume
                </button>
              )}
            </div>
          )}

          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{req.title}</h2>
          <p style={{ color: "var(--t2t-text-secondary)", fontSize: 13, marginBottom: 20 }}>{req.description}</p>

          {/* Stage Tracker */}
          {stages.length > 0 && (
            <div className="t2t-stage-track">
              {stages.map((s, i) => (
                <React.Fragment key={s.name}>
                  <div className="t2t-track-item">
                    <div className={`t2t-track-dot${s.done ? " done" : s.current ? " current" : ""}`}>
                      {s.done ? "✓" : i + 1}
                    </div>
                    <div className="t2t-track-name">{s.name}</div>
                  </div>
                  {i < stages.length - 1 && (
                    <div className={`t2t-track-line${s.done ? " done" : ""}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* T2T-03-WF1: Changes diff for vendor_change requests */}
          {requestTypeCode === "vendor_change" && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Proposed Changes</h3>
              <VendorChangesDiff oldData={vendorForDiff} newData={formData} />
            </div>
          )}

          {/* Form Data — vendor requests use tabbed VendorCreationForm, others use flat grid */}
          {VENDOR_RT_CODES.has(requestTypeCode) ? (
            Object.keys(formData).filter((k) => !k.startsWith("_")).length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Vendor Data</h3>
                <VendorCreationForm
                  formData={formData}
                  onChange={() => {}}
                  readOnly={true}
                  accessMatrix={vendorAccessMatrix || undefined}
                  errors={{}}
                  requestTypeCode={requestTypeCode}
                />
              </div>
            )
          ) : (
            Object.keys(formData).filter((k) => !k.startsWith("_")).length > 0 && (
              <>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Submitted Data</h3>
                <div className="t2t-fd-grid">
                  {Object.entries(formData).filter(([k]) => !k.startsWith("_")).map(([k, v]) => {
                    const verif = verifications.find((vf) => vf.field_id === k);
                    const badge = verif ? VERIF_BADGES[verif.status] || null : null;
                    const label = fieldLabelMap[String(k)] || k;
                    return (
                      <div className="t2t-fd-item" key={k}>
                        <div className="t2t-fd-label">{label}</div>
                        <div className="t2t-fd-value" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {v}
                          {badge && (
                            <span
                              title={verif.details || badge.label}
                              style={{ fontSize: 10, fontWeight: 700, color: badge.color, background: badge.bg, padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}
                            >
                              {badge.icon} {badge.label}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}

          {/* Attachments (Phase 13) */}
          {attachments.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Attachments</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                {attachments.map((att) => (
                  <div key={att.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#F8FAFC", borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }}>
                    <span style={{ fontSize: 18 }}>📎</span>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.original_filename}</div>
                      <div style={{ fontSize: 10, color: "#64748B" }}>
                        {att.mime_type} {att.file_size_bytes ? `• ${(att.file_size_bytes / 1024).toFixed(0)} KB` : ""}
                      </div>
                    </div>
                    <button
                      className="t2t-btn t2t-btn-outline t2t-btn-sm"
                      style={{ fontSize: 11, flexShrink: 0 }}
                      onClick={() => downloadAttachment(att.id, att.original_filename)}
                    >
                      ↓ Download
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* SAP Push Log (Phase 18) — ARCH-06: hide for non-procurement request types */}
          {!isHiddenProcurementType(requestTypeCode) && sapLogs.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>SAP Integration Log</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                {sapLogs.map((log, i) => (
                  <div key={log.id || i} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                    background: log.status === "success" ? "#F0FDF4" : log.status === "failed" ? "#FEF2F2" : "#F8FAFC",
                    border: `1px solid ${log.status === "success" ? "#BBF7D0" : log.status === "failed" ? "#FECACA" : "#E2E8F0"}`,
                  }}>
                    <span>{log.status === "success" ? "✅" : log.status === "failed" ? "❌" : "⏳"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>Attempt {log.attempt || i + 1} — {log.status}</div>
                      {log.sap_document_number && <div style={{ color: "#16A34A" }}>SAP Doc: {log.sap_document_number}</div>}
                      {log.error_message && <div style={{ color: "#DC2626" }}>{log.error_message}</div>}
                      <div style={{ color: "#94A3B8", fontSize: 10 }}>{log.created_at ? new Date(log.created_at).toLocaleString("en-IN") : ""}</div>
                    </div>
                  </div>
                ))}
                {sapLogs.some((l) => l.status === "failed") && (
                  <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ alignSelf: "flex-start" }} onClick={retrySapPush}>
                    🔄 Retry SAP Push
                  </button>
                )}
              </div>
            </>
          )}

          {/* INT-7/9 — Three-Way Match — ARCH-06: gated */}
          {!isHiddenProcurementType(requestTypeCode) && threeWayMatch && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Three-Way Match</h3>
              <div style={{
                padding: "14px 16px", borderRadius: 8, marginBottom: 20,
                background: threeWayMatch.match ? "#F0FDF4" : "#FEF2F2",
                border: `1px solid ${threeWayMatch.match ? "#BBF7D0" : "#FECACA"}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 18 }}>{threeWayMatch.match ? "✅" : "⚠️"}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: threeWayMatch.match ? "#166534" : "#DC2626" }}>
                    {threeWayMatch.match ? "Match Confirmed" : "Mismatch Detected"}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, fontSize: 12 }}>
                  <div style={{ background: "#fff", padding: "8px 10px", borderRadius: 6, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>PO Qty</div>
                    <div style={{ fontWeight: 700 }}>{threeWayMatch.po_qty ?? "—"}</div>
                  </div>
                  <div style={{ background: "#fff", padding: "8px 10px", borderRadius: 6, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>GR Qty</div>
                    <div style={{ fontWeight: 700 }}>{threeWayMatch.gr_qty ?? "—"}</div>
                  </div>
                  <div style={{ background: "#fff", padding: "8px 10px", borderRadius: 6, textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: "#64748B", fontWeight: 600 }}>Invoice Qty</div>
                    <div style={{ fontWeight: 700 }}>{threeWayMatch.invoice_qty ?? "—"}</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, fontSize: 11 }}>
                  <div style={{ background: "#fff", padding: "6px 10px", borderRadius: 6 }}>
                    PO Amount: <strong>{"\u20B9"}{threeWayMatch.po_amount ?? "—"}</strong> &middot; Invoice: <strong>{"\u20B9"}{threeWayMatch.invoice_amount ?? "—"}</strong>
                  </div>
                  <div style={{ background: "#fff", padding: "6px 10px", borderRadius: 6 }}>
                    Qty Var: <strong style={{ color: Math.abs(threeWayMatch.qty_variance_pct) > 1 ? "#DC2626" : "#16A34A" }}>{threeWayMatch.qty_variance_pct ?? 0}%</strong>
                    &middot; Amt Var: <strong style={{ color: Math.abs(threeWayMatch.amt_variance_pct) > 2 ? "#DC2626" : "#16A34A" }}>{threeWayMatch.amt_variance_pct ?? 0}%</strong>
                  </div>
                </div>
                {threeWayMatch.reason && !threeWayMatch.match && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "#DC2626" }}>{threeWayMatch.reason}</div>
                )}
              </div>
            </>
          )}

          {/* INT-8 — e-Invoice IRN + QR Code — ARCH-06: gated */}
          {!isHiddenProcurementType(requestTypeCode) && einvoice && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>e-Invoice (GSTN)</h3>
              <div style={{ padding: "14px 16px", borderRadius: 8, marginBottom: 20, background: "#F8FAFC", border: "1px solid var(--t2t-border)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", padding: "2px 8px", borderRadius: 10,
                        color: einvoice.status === "generated" ? "#166534" : einvoice.status === "cancelled" ? "#DC2626" : "#D97706",
                        background: einvoice.status === "generated" ? "#DCFCE7" : einvoice.status === "cancelled" ? "#FEE2E2" : "#FEF3C7" }}>
                        {einvoice.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, marginBottom: 4 }}><strong>IRN:</strong> <span style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>{einvoice.irn}</span></div>
                    <div style={{ fontSize: 11, color: "#64748B" }}>Ack No: {einvoice.ack_number} &middot; Ack Date: {einvoice.ack_date}</div>
                    {einvoice.status === "generated" && (
                      <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" style={{ marginTop: 8, fontSize: 10, color: "#DC2626" }} onClick={cancelEinvoice}>Cancel IRN</button>
                    )}
                  </div>
                  {einvoice.signed_qr_code && (
                    <div style={{ width: 80, height: 80, background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                      <img src={`data:image/png;base64,${einvoice.signed_qr_code}`} alt="QR" style={{ width: "100%", height: "100%" }} />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* INT-10/11 — Ariba Push Log + Sourcing — ARCH-06: gated */}
          {!isHiddenProcurementType(requestTypeCode) && aribaLogs.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Ariba Integration Log</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                {aribaLogs.map((log, i) => (
                  <div key={log.id || i} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                    background: log.status === "success" ? "#F0FDF4" : log.status === "failed" ? "#FEF2F2" : "#F8FAFC",
                    border: `1px solid ${log.status === "success" ? "#BBF7D0" : log.status === "failed" ? "#FECACA" : "#E2E8F0"}`,
                  }}>
                    <span>{log.status === "success" ? "✅" : log.status === "failed" ? "❌" : "⏳"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{log.ariba_action || "Ariba Push"} — {log.status}</div>
                      {log.ariba_doc_id && <div style={{ color: "#F0AB00" }}>Ariba Doc: {log.ariba_doc_id}</div>}
                      {log.ariba_sourcing_id && <div style={{ color: "#2563EB" }}>Sourcing Event: {log.ariba_sourcing_id}</div>}
                      <div style={{ color: "#94A3B8", fontSize: 10 }}>{log.created_at ? new Date(log.created_at).toLocaleString("en-IN") : ""}</div>
                    </div>
                  </div>
                ))}
                {aribaLogs.some((l) => l.status === "failed") && (
                  <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ alignSelf: "flex-start" }} onClick={retryAribaPush}>
                    🔄 Retry Ariba Push
                  </button>
                )}
              </div>
            </>
          )}

          {/* INT-14 — GeM Order Status — ARCH-06: gated */}
          {!isHiddenProcurementType(requestTypeCode) && gemStatus && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>GeM Order</h3>
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 20, background: "#F0FDF4", border: "1px solid #BBF7D0", fontSize: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>🏛️</span>
                  <span style={{ fontWeight: 600 }}>Order ID: {gemStatus.gem_order_id}</span>
                  <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: "#16A34A", background: "#DCFCE7", padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>{gemStatus.status}</span>
                </div>
                {gemStatus.order_date && <div style={{ color: "#64748B", fontSize: 11, marginTop: 4 }}>Placed: {new Date(gemStatus.order_date).toLocaleDateString("en-IN")}</div>}
              </div>
            </>
          )}

          {/* INT-15 — TReDS Status — ARCH-06: gated */}
          {!isHiddenProcurementType(requestTypeCode) && tredsStatus && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>TReDS Discounting</h3>
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 20, background: "#FFFBEB", border: "1px solid #FDE68A", fontSize: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>💰</span>
                  <span style={{ fontWeight: 600 }}>{tredsStatus.platform || "TReDS"}</span>
                  <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700,
                    color: tredsStatus.discounting_status === "completed" ? "#16A34A" : "#D97706",
                    background: tredsStatus.discounting_status === "completed" ? "#DCFCE7" : "#FEF3C7",
                    padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>
                    {tredsStatus.discounting_status || tredsStatus.upload_status}
                  </span>
                </div>
                <div style={{ color: "#64748B", fontSize: 11, marginTop: 4 }}>
                  Upload: {tredsStatus.upload_status || "—"}
                  {tredsStatus.uploaded_at && <span> &middot; {new Date(tredsStatus.uploaded_at).toLocaleDateString("en-IN")}</span>}
                </div>
              </div>
            </>
          )}

          {/* Timeline */}
          {timeline.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Activity Timeline</h3>
              <div className="t2t-timeline">
                {timeline.map((tl, i) => (
                  <div className="t2t-tl-item" key={i}>
                    <div className={`t2t-tl-dot ${DOT_CLASS[tl.type] || ""}`}>
                      {tl.type === "approve" || tl.type === "system_action_success" ? "✓"
                        : tl.type === "reject" || tl.type === "system_action_failure" ? "✗"
                        : tl.type === "submit" ? "↑"
                        : tl.type === "system_action_execute" ? "⚡"
                        : "→"}
                    </div>
                    <div className="t2t-tl-body">
                      <div className="t2t-tl-action">{tl.action}</div>
                      <div className="t2t-tl-meta">{tl.actor} &bull; {tl.time}</div>
                      {tl.comment && <div className="t2t-tl-comment">{tl.comment}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="t2t-req-sidebar">
          <div style={{ background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius-lg)", padding: 18, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Request Info</div>
            {[
              { label: "Status",       value: <span className={`t2t-badge t2t-badge-${req.status}`}>{(req.status || "").replace("_", " ")}</span> },
              { label: "Workflow",     value: req.workflow_name },
              { label: "Submitted By", value: req.submitted_by },
              { label: "Submitted On", value: req.submitted_on },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: 12 }}>
                <span style={{ color: "var(--t2t-text-muted)", fontWeight: 600 }}>{label}</span>
                <span style={{ fontWeight: 600 }}>{value}</span>
              </div>
            ))}
          </div>

          {req.can_act && (
            <div style={{ background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius-lg)", padding: 18 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12 }}>Your Action</div>

              {/* MM1 — mismatch severity banner */}
              {mismatchSummary?.worst_severity === "blocking" && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 6, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: "#DC2626" }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>🛑 Blocking Mismatch</div>
                  <div>Data verification score is below 50%. Resolve the mismatch before approving.</div>
                </div>
              )}
              {mismatchSummary?.worst_severity === "high_warning" && (
                <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "10px 12px", marginBottom: 10, fontSize: 12, color: "#92400E" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ High Warning Mismatch</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={mismatchAcknowledged} onChange={(e) => setMismatchAcknowledged(e.target.checked)} />
                    I acknowledge this data mismatch and confirm approval
                  </label>
                </div>
              )}
              {mismatchSummary?.worst_severity === "low_warning" && (
                <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontSize: 11, color: "#D97706" }}>
                  ⚠️ Low Warning — minor data mismatch detected. Workflow can continue.
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  className="t2t-btn t2t-btn-success t2t-btn-block"
                  disabled={
                    mismatchSummary?.worst_severity === "blocking" ||
                    (mismatchSummary?.worst_severity === "high_warning" && !mismatchAcknowledged)
                  }
                  style={mismatchSummary?.worst_severity === "blocking" ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                  onClick={() => { setComment(""); setActionError(""); setActionModal("approve"); }}
                >✓ Approve</button>
                <button className="t2t-btn t2t-btn-danger  t2t-btn-block" onClick={() => {
                  setComment(""); setActionError(""); setRejTargetId(""); setRejTargets([]);
                  setActionModal("reject");
                  // Fetch allowed return stages
                  setRejLoading(true);
                  postData(`t2t/requests/${id}/rejection-targets`, {}).then((resp) => {
                    if (!resp.error) {
                      const raw = resp.data?.data;
                      setRejTargets(Array.isArray(raw) ? raw : []);
                    }
                    setRejLoading(false);
                  });
                }}>✗ Reject</button>
                <button className="t2t-btn t2t-btn-ghost   t2t-btn-block" onClick={() => { setComment(""); setActionError(""); setActionModal("reassign"); }}>→ Reassign</button>
                <button className="t2t-btn t2t-btn-block" style={{ background: "#D97706", color: "#fff", border: "none" }} onClick={() => { setComment(""); setActionError(""); setResumeDate(""); setActionModal("hold"); }}>⏸ Hold</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {actionModal && (
        <div className="t2t-overlay" onClick={() => setActionModal(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()}>
            <div className="t2t-modal-head">
              <div>
                <div className="t2t-modal-title">
                  {{ approve: "✓ Approve Request", reject: "✗ Reject Request", reassign: "→ Reassign Request", hold: "⏸ Put Request On Hold" }[actionModal]}
                </div>
                <div className="t2t-modal-sub">{req.request_id} — {req.title}</div>
              </div>
              <button className="t2t-close-btn" onClick={() => { setActionModal(null); setActionError(""); setComment(""); setBlockVendor(false); setMismatchAcknowledged(false); }}>×</button>
            </div>
            <div className="t2t-modal-body">
              {actionError && (
                <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{actionError}</div>
              )}

              {/* Phase 9 — Return stage picker for reject */}
              {actionModal === "reject" && (
                <div className="t2t-fg">
                  <label>Return to Stage *</label>
                  {rejLoading ? (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: "8px 0" }}>Loading stages...</div>
                  ) : rejTargets.length > 0 ? (
                    <>
                      {/* Quick option: previous stage */}
                      {rejTargets.some((t) => t.is_previous_stage) && (
                        <button
                          type="button"
                          className={`t2t-btn t2t-btn-sm ${rejTargetId === rejTargets.find((t) => t.is_previous_stage)?.id ? "t2t-btn-primary" : "t2t-btn-outline"}`}
                          style={{ marginBottom: 8, width: "100%", textAlign: "left" }}
                          onClick={() => setRejTargetId(rejTargets.find((t) => t.is_previous_stage)?.id || "")}
                        >
                          ↩ Return to Previous Stage
                        </button>
                      )}
                      <select
                        className="t2t-fc"
                        value={rejTargetId}
                        onChange={(e) => { setRejTargetId(e.target.value); if (actionError) setActionError(""); }}
                      >
                        <option value="">Choose a stage to return to...</option>
                        {rejTargets.map((t) => (
                          <option key={t.id} value={t.id}>{t.label || t.name}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: "8px 0" }}>
                      No return stages available.
                    </div>
                  )}
                </div>
              )}

              {/* Hold — resume date */}
              {actionModal === "hold" && (
                <div className="t2t-fg">
                  <label>Expected Resume Date (optional)</label>
                  <input className="t2t-fc" type="date" value={resumeDate} onChange={(e) => setResumeDate(e.target.value)} />
                </div>
              )}

              {/* T2T-03-WF1: block vendor option for vendor_change approvals */}
              {actionModal === "approve" && requestTypeCode === "vendor_change" && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 10, padding: "8px 10px", background: "#FEF2F2", borderRadius: 6, border: "1px solid #FECACA", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={blockVendor}
                    onChange={(e) => setBlockVendor(e.target.checked)}
                  />
                  <span style={{ color: "#DC2626", fontWeight: 600 }}>Also block this vendor after approval</span>
                </label>
              )}
              <div className="t2t-fg">
                <label>
                  {actionModal === "reassign" ? "Comment (optional)" : actionModal === "reject" ? "Reason for Rejection * (min 5 characters)" : actionModal === "hold" ? "Why are you putting this on hold? * (min 5 characters)" : "Comment * (min 5 characters)"}
                </label>
                <textarea
                  className="t2t-fc"
                  rows="3"
                  placeholder="Add a note..."
                  value={comment}
                  onChange={(e) => { setComment(e.target.value); if (actionError) setActionError(""); }}
                />
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => { setActionModal(null); setActionError(""); setComment(""); setBlockVendor(false); }}>Cancel</button>
              <button
                className={`t2t-btn ${actionModal === "approve" ? "t2t-btn-success" : actionModal === "reject" ? "t2t-btn-danger" : "t2t-btn-primary"}`}
                onClick={handleAction}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default T2TRequestDetail;
